import { useState } from "react";
import { motion } from "framer-motion";
import { track } from "@/lib/analytics";
import { Check, ArrowRight } from "lucide-react";

const APP_URL = import.meta.env.VITE_APP_URL || "https://app.coach-os.eu";
const DISCOUNT = 0.15;
const EASE = [0.16, 1, 0.3, 1] as const;

const plans = [
  {
    name: "Starter",
    monthlyPrice: 39,
    priceId: import.meta.env.STRIPE_PRICE_ID_STARTER || import.meta.env.VITE_STRIPE_PRICE_ID_STARTER || "price_1TZwUIAGumQ4Hu5VlCnscUE9",
    description: "Pour les coachs qui démarrent",
    features: ["Clients illimités", "15 programmes IA / mois", "15 plans nutrition IA / mois", "Résumés & vigilance IA clients illimités", "Messagerie & app mobile clients", "Suivi, dashboard & KPIs"],
    featured: false,
  },
  {
    name: "Pro",
    monthlyPrice: 79,
    priceId: import.meta.env.STRIPE_PRICE_ID_PRO || import.meta.env.VITE_STRIPE_PRICE_ID_PRO || "price_1TZwUFAGumQ4Hu5VRvHQrX5D",
    description: "Pour développer votre activité",
    features: ["Tout du plan Starter", "Assistant IA — 300 messages / mois", "30 programmes + 30 plans nutrition IA / mois", "Bilans IA & export PDF", "Automatisations avancées", "Support prioritaire"],
    featured: true,
  },
  {
    name: "Business",
    monthlyPrice: 189,
    priceId: import.meta.env.STRIPE_PRICE_ID_BUSINESS || import.meta.env.VITE_STRIPE_PRICE_ID_BUSINESS || "price_1TZwUGAGumQ4Hu5VCdp0gs2Q",
    description: "Pour les coachs les plus exigeants",
    features: ["Tout du plan Pro", "Profitez de l'IA à fond — tout en illimité", "Personnalisation des rapports de suivi"],
    featured: false,
  },
];

const Pricing = () => {
  const [annual, setAnnual] = useState(false);

  function handleCheckout(plan: (typeof plans)[0]) {
    track("pricing_checkout_clicked", {
      plan: plan.name,
      billing: annual ? "annual" : "monthly",
      monthly_price: plan.monthlyPrice,
      displayed_price: annual ? Math.round(plan.monthlyPrice * (1 - DISCOUNT)) : plan.monthlyPrice,
      has_price_id: Boolean(plan.priceId),
    });
    if (!plan.priceId) {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.location.href = `${APP_URL}/signup?plan=${plan.name.toLowerCase()}&billing=${annual ? "annual" : "monthly"}`;
  }

  return (
    <section id="pricing" className="relative overflow-hidden py-16 md:py-24">
      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}
          className="mx-auto mb-14 max-w-2xl text-center"
        >
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-[#2563EB]">Tarifs</p>
          <h2 className="landing-section-title text-3xl font-extrabold leading-tight tracking-tight text-[#0e1220] md:text-5xl">
            Des tarifs{" "}
            <span className="text-[#2563EB]">simples.</span>
            <span className="align-middle ml-2 rounded-full border border-[#dfe2ec] bg-[#eef0f6] px-2.5 py-1 text-sm font-bold text-[#0e1220]/60">TTC</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[#0e1220]/60">Choisissez un point de départ. Sans engagement, résiliable à tout moment.</p>

          {/* Toggle Mensuel / Annuel */}
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-[#e7e9f1] bg-[#eef0f6] p-1">
            <button
              aria-pressed={!annual}
              onClick={() => setAnnual(false)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${!annual ? "bg-white text-[#0e1220] shadow-[0_1px_3px_rgba(16,20,40,0.08)]" : "text-[#0e1220]/55 hover:text-[#0e1220]"}`}
            >
              Mensuel
            </button>
            <button
              aria-pressed={annual}
              onClick={() => setAnnual(true)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${annual ? "bg-white text-[#0e1220] shadow-[0_1px_3px_rgba(16,20,40,0.08)]" : "text-[#0e1220]/55 hover:text-[#0e1220]"}`}
            >
              Annuel
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${annual ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#2563EB]/15 text-[#2563EB]"}`}>−15%</span>
            </button>
          </div>
        </motion.div>

        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
          {plans.map((plan, i) => {
            const displayPrice = annual ? Math.round(plan.monthlyPrice * (1 - DISCOUNT)) : plan.monthlyPrice;
            return (
              <motion.div
                key={plan.name}
                data-featured={plan.featured || undefined}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
                className={`relative flex flex-col rounded-3xl border p-7 transition-transform hover:-translate-y-1 ${
                  plan.featured ? "border-[#2563EB]" : "border-[#e7e9f1]"
                }`}
                style={
                  plan.featured
                    ? { background: "#eef1ff", boxShadow: "0 24px 60px -30px rgba(37, 99, 235,0.35)" }
                    : { background: "#ffffff", boxShadow: "0 1px 3px rgba(16,20,40,0.05)" }
                }
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-bold text-white shadow-[0_8px_20px_-6px_rgba(37, 99, 235,0.5)]"
                    style={{ background: "linear-gradient(135deg, #2563EB, #2563EB)" }}>
                    Populaire
                  </span>
                )}

                <h3 className="text-lg font-bold text-[#0e1220]">{plan.name}</h3>
                <p className="mt-1 min-h-10 text-sm text-[#0e1220]/60">{plan.description}</p>

                <div className="mt-6 mb-1 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold tracking-tight text-[#0e1220]">{displayPrice}€</span>
                  <span className="text-sm text-[#0e1220]/55">/mois TTC</span>
                </div>
                <p className="mb-5 min-h-10 text-sm text-[#0e1220]/50">
                  {annual ? (
                    <>Facturé {Math.round(plan.monthlyPrice * 12 * (1 - DISCOUNT))}€/an · <span className="text-[#2563EB] font-semibold">15 % de réduction</span></>
                  ) : (
                    <>ou {Math.round(plan.monthlyPrice * (1 - DISCOUNT))}€/mois en annuel</>
                  )}
                </p>

                <button
                  onClick={() => handleCheckout(plan)}
                  className={`group mb-7 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl font-semibold transition-transform hover:scale-[1.02] ${
                    plan.featured ? "text-white shadow-[0_10px_24px_-16px_rgba(20,20,43,0.5)]" : "text-[#0e1220] border border-[#dfe2ec] bg-white hover:bg-[#eef0f6]"
                  }`}
                  style={plan.featured ? { background: "#2563EB" } : undefined}
                >
                  Commencer
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>

                <ul className="space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#2563EB]/15">
                        <Check className="h-3 w-3 text-[#2563EB]" />
                      </span>
                      <span className="text-[#0e1220]/75">{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
